/**
 * Color3DService — 3D volume rendering transfer-function presets.
 *
 * Owns the active 3D color preset and builds the vtk color / opacity transfer
 * functions that volume viewports apply to their volume pipeline. Broadcasts
 * COLOR_3D_CHANGED whenever the effective preset changes so viewports can
 * re-apply the transfer functions and re-render.
 *
 * While a C-arm draft edit is active, the 'virtual-angio' preset temporarily
 * overrides the base preset (fluoroscopy-like preview of what the C-arm view
 * would show); committing or cancelling the draft restores the base preset.
 *
 * On top of the preset, the user can apply a window/level to the transfer
 * function (the desktop "3D Colors" control): the level slides the whole
 * colormap along the intensity axis, the window stretches it.
 *
 * The adjustment is kept per slot and never dropped by a preset change, so each
 * contrast context comes back exactly as it was left: switching presets in the
 * tool and back restores the first preset's own values, and a C-arm draft keeps
 * its own contrast without touching the values the same preset holds when the
 * user picks it from the tool.
 */

import vtkPiecewiseFunction from '@kitware/vtk.js/Common/DataModel/PiecewiseFunction';
import vtkColorTransferFunction from '@kitware/vtk.js/Rendering/Core/ColorTransferFunction';
import type vtkVolumeProperty from '@kitware/vtk.js/Rendering/Core/VolumeProperty';

import { CarmViewService } from './carm-view-service';
import {
    COLOR_3D_LEVEL_RANGE,
    COLOR_3D_WINDOW_RANGE,
    DEFAULT_COLOR_3D_PRESET_ID,
    getColor3DPreset,
} from './color-3d-presets';
import type { Color3DPreset, Color3DPresetId } from './color-3d-presets';
import type { WindowLevel } from './window-level-service';
import { PubSubService } from '../core/pubsub-service';
import type { ServiceContext, ServiceRegistration } from '../core/types';

/**
 * vtk transfer functions built from a preset. The caller owns the returned
 * objects and must delete() them once replaced or disposed.
 */
export interface VolumeTransferFunctions {
    colorFunction: vtkColorTransferFunction;
    opacityFunction: vtkPiecewiseFunction;
}

/** Payload type for COLOR_3D_CHANGED events. */
export interface Color3DChangedPayload {
    presetId: Color3DPresetId;
}

/** Payload type for WINDOW_LEVEL_CHANGED events. */
export interface Color3DWindowLevelChangedPayload extends WindowLevel {
    /** Preset the adjustment applies to (the effective one at the time). */
    presetId: Color3DPresetId;
}

export class Color3DService extends PubSubService {
    static readonly SERVICE_NAME = 'Color3DService';

    static readonly REGISTRATION: ServiceRegistration = {
        name: Color3DService.SERVICE_NAME,
        factory: (context: ServiceContext) => new Color3DService(context),
    };

    readonly EVENTS = {
        COLOR_3D_CHANGED: 'color3d:changed',
        WINDOW_LEVEL_CHANGED: 'color3d:windowlevel:changed',
    } as const;

    /** Base preset — workflow/user selected. */
    private _presetId: Color3DPresetId = DEFAULT_COLOR_3D_PRESET_ID;

    /** Temporary override while a C-arm draft is active; null otherwise. */
    private _overridePresetId: Color3DPresetId | null = null;

    /** Window/level per adjustment slot. Missing means use the authored values. */
    private _adjustments = new Map<string, WindowLevel>();

    /** CarmViewService subscriptions, established lazily on first pipeline use. */
    private _carmUnsubs: Array<() => void> = [];

    private readonly context: ServiceContext;

    constructor(context: ServiceContext) {
        super();
        this.context = context;
    }

    // ========================================================================
    // Presets
    // ========================================================================

    /** Effective preset id: the C-arm override while a draft is active, else the base preset. */
    getActivePresetId(): Color3DPresetId {
        return this._overridePresetId ?? this._presetId;
    }

    getActivePreset(): Color3DPreset {
        // Ids are validated on entry (applyPreset) — always resolvable
        return getColor3DPreset(this.getActivePresetId())!;
    }

    /**
     * Resolve a specific preset — used by viewports that pin their rendering
     * profile instead of following the active preset (e.g. report views).
     * Unknown ids fall back to the active preset rather than breaking rendering.
     */
    getPreset(presetId: Color3DPresetId): Color3DPreset {
        return getColor3DPreset(presetId) ?? this.getActivePreset();
    }

    /**
     * Set the base preset (broadcasts COLOR_3D_CHANGED → viewports re-render).
     * Unknown ids are ignored — this only guards against programming errors,
     * never crashes rendering.
     *
     * The preset's stored adjustment is kept: a preset switch is a change of
     * view, not a reset, so coming back to a preset renders the contrast it was
     * left at. Only "reset to default" returns a preset to its authored values.
     */
    applyPreset(presetId: Color3DPresetId): void {
        if (!getColor3DPreset(presetId)) {
            console.warn(`[Color3DService] Unknown preset id: "${presetId}"`);
            return;
        }

        const before = this.getActivePresetId();
        this._presetId = presetId;

        // A preset change makes viewports re-apply the whole profile, adjustment
        // included. Re-picking the active preset, or picking the base preset
        // while a C-arm override governs rendering, changes nothing on screen.
        this.broadcastIfChanged(before);
    }

    // ========================================================================
    // Window / level
    // ========================================================================

    /**
     * Slot the current adjustment belongs to. The C-arm override holds its own,
     * separate from the values the same preset carries when the user picks it
     * from the tool: tuning the draft's contrast must not retune the tool's.
     */
    private adjustmentKey(): string {
        return this._overridePresetId ? `override:${this._overridePresetId}` : `base:${this._presetId}`;
    }

    /** Effective preset's live window/level — its authored values unless adjusted. */
    getWindowLevel(): WindowLevel {
        return this._adjustments.get(this.adjustmentKey()) ?? this.getDefaultWindowLevel();
    }

    /** The window/level resetWindowLevel() restores: the effective preset's authored values. */
    getDefaultWindowLevel(): WindowLevel {
        const preset = this.getActivePreset();
        return { window: preset.window, level: preset.level };
    }

    /** Whether the current slot carries a user adjustment. */
    isAdjusted(): boolean {
        return this._adjustments.has(this.adjustmentKey());
    }

    /**
     * Adjust the effective preset's transfer-function window/level
     * (broadcasts WINDOW_LEVEL_CHANGED → volume viewports refresh their points).
     *
     * Both values are clamped to the tool's range here rather than at each call
     * site, so the drag and the slider can never disagree about the live value —
     * and a long drag can't push the colormap clear of the data, leaving an
     * empty volume the slider then snaps back by hundreds of HU.
     */
    setWindowLevel(window: number, level: number): void {
        const clampedWindow = Color3DService.clampTo(window, COLOR_3D_WINDOW_RANGE);
        const clampedLevel = Color3DService.clampTo(level, COLOR_3D_LEVEL_RANGE);

        const current = this.getWindowLevel();
        if (clampedWindow === current.window && clampedLevel === current.level) return;

        this._adjustments.set(this.adjustmentKey(), { window: clampedWindow, level: clampedLevel });

        this.broadcast(this.EVENTS.WINDOW_LEVEL_CHANGED, {
            presetId: this.getActivePresetId(),
            window: clampedWindow,
            level: clampedLevel,
        } satisfies Color3DWindowLevelChangedPayload);
    }

    /** Slide the colormap along the intensity axis, keeping the current window. */
    setLevel(level: number): void {
        this.setWindowLevel(this.getWindowLevel().window, level);
    }

    /** Drop the current slot's adjustment, back to the preset's authored window/level. */
    resetWindowLevel(): void {
        if (!this._adjustments.delete(this.adjustmentKey())) return;

        this.broadcastEffectiveWindowLevel();
    }

    /**
     * Announce the effective window/level, so viewports rewrite their
     * transfer-function points without rebuilding the whole profile.
     */
    private broadcastEffectiveWindowLevel(): void {
        const { window, level } = this.getWindowLevel();
        this.broadcast(this.EVENTS.WINDOW_LEVEL_CHANGED, {
            presetId: this.getActivePresetId(),
            window,
            level,
        } satisfies Color3DWindowLevelChangedPayload);
    }

    // ========================================================================
    // Transfer function building
    // ========================================================================

    /**
     * Build fresh vtk transfer functions from the active preset, or from
     * `presetId` when a caller pins its profile.
     * The caller owns the returned objects (delete() on re-apply/dispose).
     */
    createTransferFunctions(presetId?: Color3DPresetId): VolumeTransferFunctions {
        const opacityFunction = vtkPiecewiseFunction.newInstance();
        const colorFunction = vtkColorTransferFunction.newInstance();
        this.writePoints({ colorFunction, opacityFunction }, presetId);
        return { colorFunction, opacityFunction };
    }

    /**
     * Rewrite the points of existing transfer functions for the current
     * window/level — the drag/slider hot path. Reuses the vtk objects already
     * plugged into the volume property: re-adding the points marks them
     * modified, which is enough to trigger a re-render.
     */
    refreshTransferFunctions(functions: VolumeTransferFunctions, presetId?: Color3DPresetId): void {
        functions.opacityFunction.removeAllPoints();
        functions.colorFunction.removeAllPoints();
        this.writePoints(functions, presetId);
    }

    /**
     * Fill transfer functions with the preset's points, abscissae shifted by the
     * window/level adjustment. A pinned `presetId` uses that preset's authored
     * values — pinned viewports (report views) never follow the user's
     * adjustment, just as they never follow the active preset.
     */
    private writePoints(functions: VolumeTransferFunctions, presetId?: Color3DPresetId): void {
        const preset = presetId ? this.getPreset(presetId) : this.getActivePreset();
        const windowLevel = presetId ? { window: preset.window, level: preset.level } : this.getWindowLevel();

        for (const point of preset.opacityPoints) {
            functions.opacityFunction.addPoint(
                Color3DService.adjustValue(point.value, preset, windowLevel),
                point.opacity
            );
        }

        for (const point of preset.colorPoints) {
            functions.colorFunction.addRGBPoint(
                Color3DService.adjustValue(point.value, preset, windowLevel),
                point.r,
                point.g,
                point.b
            );
        }
    }

    /**
     * Re-center a control point on the new level and stretch it by the window
     * ratio: `x' = level + (x - levelInit) * (window / windowInit)`. With the
     * window unchanged this is a pure translation. RGB and opacity values are
     * never touched — the tool moves the colormap, it does not recolor it.
     */
    private static adjustValue(value: number, preset: Color3DPreset, windowLevel: WindowLevel): number {
        return windowLevel.level + (value - preset.level) * (windowLevel.window / preset.window);
    }

    private static clampTo(value: number, range: { min: number; max: number }): number {
        return Math.min(Math.max(value, range.min), range.max);
    }

    /**
     * Apply a preset to a volume property: fresh transfer functions plus
     * shading, material and gradient-opacity settings. Returns the created
     * transfer functions — the caller owns their lifetime.
     *
     * Without `presetId` the active preset is used and the C-arm override is
     * wired up; a pinned preset skips both — it never follows the override.
     */
    applyToVolumeProperty(property: vtkVolumeProperty, presetId?: Color3DPresetId): VolumeTransferFunctions {
        if (!presetId) this.ensureCarmSubscriptions();

        const preset = presetId ? this.getPreset(presetId) : this.getActivePreset();
        const functions = this.createTransferFunctions(presetId);

        property.setRGBTransferFunction(0, functions.colorFunction);
        property.setScalarOpacity(0, functions.opacityFunction);
        property.setShade(preset.shade);
        property.setAmbient(preset.ambient);
        property.setDiffuse(preset.diffuse);
        property.setSpecular(preset.specular);
        property.setSpecularPower(preset.specularPower);

        const ramp = preset.gradientOpacity;
        property.setUseGradientOpacity(0, ramp !== null);
        if (ramp) {
            property.setGradientOpacityMinimumValue(0, ramp.minValue);
            property.setGradientOpacityMinimumOpacity(0, ramp.minOpacity);
            property.setGradientOpacityMaximumValue(0, ramp.maxValue);
            property.setGradientOpacityMaximumOpacity(0, ramp.maxOpacity);
        }

        property.setInterpolationTypeToLinear();

        return functions;
    }

    // ========================================================================
    // C-arm draft override
    // ========================================================================

    /**
     * While a C-arm draft is being edited, volumes render with the
     * virtual-angio preset. Subscribed lazily on first applyToVolumeProperty —
     * the override only matters once a 3D volume pipeline exists — and torn
     * down on reset(): resetAll() clears the sibling's listener map, so the
     * next case re-wires here when its volume viewport applies its preset.
     */
    private ensureCarmSubscriptions(): void {
        if (this._carmUnsubs.length > 0) return;

        const carm = this.context.services.getService<CarmViewService>(CarmViewService.SERVICE_NAME);
        this._carmUnsubs.push(
            // An earlier EDIT_STARTED subscriber may cancel the draft
            // synchronously (e.g. no 3D viewport) — once the draft is gone,
            // EDIT_CANCELLED has already fired and would never clear an
            // override applied here, so don't apply one
            carm.subscribe(carm.EVENTS.EDIT_STARTED, () => {
                if (carm.getDraft()) this.setOverride('virtual-angio');
            }),
            carm.subscribe(carm.EVENTS.EDIT_COMMITTED, () => this.setOverride(null)),
            carm.subscribe(carm.EVENTS.EDIT_CANCELLED, () => this.setOverride(null))
        );
    }

    private setOverride(presetId: Color3DPresetId | null): void {
        if (this._overridePresetId === presetId) return;

        const before = this.getActivePresetId();
        const beforeWindowLevel = this.getWindowLevel();
        this._overridePresetId = presetId;

        // A preset change re-applies the whole profile, adjustment included
        if (this.broadcastIfChanged(before)) return;

        // Same preset on both sides — the base preset is the one the draft
        // overrides with. The slot still changed, so the contrast may have moved
        // even though the profile didn't: refresh the points.
        const { window, level } = this.getWindowLevel();
        if (window !== beforeWindowLevel.window || level !== beforeWindowLevel.level) {
            this.broadcastEffectiveWindowLevel();
        }
    }

    /** Broadcast COLOR_3D_CHANGED if the effective preset moved; returns whether it did. */
    private broadcastIfChanged(before: Color3DPresetId): boolean {
        const presetId = this.getActivePresetId();
        if (presetId === before) return false;

        this.broadcast(this.EVENTS.COLOR_3D_CHANGED, { presetId } satisfies Color3DChangedPayload);
        return true;
    }

    // ========================================================================
    // Lifecycle
    // ========================================================================

    override reset(): void {
        this._carmUnsubs.forEach((unsub) => unsub());
        this._carmUnsubs.length = 0;
        this._presetId = DEFAULT_COLOR_3D_PRESET_ID;
        this._overridePresetId = null;
        this._adjustments.clear();
        super.reset();
    }
}
